import React, { useEffect, useState } from 'react';
import {
  Rocket, Building2, LayoutList, Facebook, BarChart3, FolderOpen, HelpCircle,
  ChevronDown, Lightbulb, AlertTriangle, CheckCircle2, ExternalLink,
} from 'lucide-react';

/**
 * Marveland 操作手冊（分頁式互動網頁）—— 給 agent / staff 的培訓手冊
 * 路由：/manual#quick-start 等；hash 直達分頁。內容無帳密等敏感資訊，可公開連結分享。
 */

type ChapterId = 'start' | 'create' | 'manage' | 'social' | 'analytics' | 'media' | 'faq';

const CHAPTERS: { id: ChapterId; label: string; icon: React.ReactNode }[] = [
  { id: 'start', label: '快速開始', icon: <Rocket className="h-4 w-4" /> },
  { id: 'create', label: '新增物業', icon: <Building2 className="h-4 w-4" /> },
  { id: 'manage', label: '管理物業', icon: <LayoutList className="h-4 w-4" /> },
  { id: 'social', label: '社群推廣 FB', icon: <Facebook className="h-4 w-4" /> },
  { id: 'analytics', label: '看成效數據', icon: <BarChart3 className="h-4 w-4" /> },
  { id: 'media', label: '媒體庫', icon: <FolderOpen className="h-4 w-4" /> },
  { id: 'faq', label: '常見問題', icon: <HelpCircle className="h-4 w-4" /> },
];

function chapterFromHash(): ChapterId {
  const h = window.location.hash.replace('#', '');
  return (CHAPTERS.some((c) => c.id === h) ? h : 'start') as ChapterId;
}

// ---------- 內容元件 ----------
const Tip: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex gap-2 rounded-lg border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
    <Lightbulb className="mt-0.5 h-4 w-4 shrink-0 text-blue-500" />
    <div>{children}</div>
  </div>
);
const Warn: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900">
    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-red-500" />
    <div>{children}</div>
  </div>
);
const Done: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="flex gap-2 rounded-lg border border-green-200 bg-green-50 p-3 text-sm text-green-900">
    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
    <div>{children}</div>
  </div>
);

const Shot: React.FC<{ src: string; caption: string }> = ({ src, caption }) => (
  <figure className="overflow-hidden rounded-xl border border-slate-200 shadow-sm">
    <img src={src} alt={caption} loading="lazy" className="w-full bg-white" />
    <figcaption className="bg-slate-50 px-3 py-2 text-xs text-slate-500">{caption}</figcaption>
  </figure>
);

const Step: React.FC<{ n: number; title: string; children?: React.ReactNode }> = ({ n, title, children }) => (
  <div className="flex gap-3">
    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-blue-800 font-bold text-white">{n}</span>
    <div className="min-w-0 flex-1 pb-5">
      <h4 className="mb-1 pt-1 font-semibold text-slate-900">{title}</h4>
      {children && <div className="space-y-3 text-sm leading-relaxed text-slate-600">{children}</div>}
    </div>
  </div>
);

const Faq: React.FC<{ q: string; children: React.ReactNode }> = ({ q, children }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-slate-200">
      <button onClick={() => setOpen((v) => !v)} className="flex w-full items-center justify-between p-4 text-left">
        <span className="font-semibold text-slate-800">{q}</span>
        <ChevronDown className={`h-4 w-4 shrink-0 text-slate-400 transition ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && <div className="border-t border-slate-100 p-4 text-sm leading-relaxed text-slate-600">{children}</div>}
    </div>
  );
};

const AdminLink: React.FC = () => (
  <a href="https://marveland.hk/admin" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 font-semibold text-blue-700 underline">
    marveland.hk/admin <ExternalLink className="h-3.5 w-3.5" />
  </a>
);

// ---------- 各章節 ----------
const StartChapter = () => (
  <div>
    <h2 className="mb-1 text-2xl font-bold">快速開始</h2>
    <p className="mb-6 text-sm text-slate-500">第一次使用？跟着以下三步，五分鐘上手。</p>
    <Step n={1} title="登入後台">
      <p>用瀏覽器打開 <AdminLink />，輸入公司派發的帳號和密碼。</p>
      <Tip>建議用手機或電腦把後台「加到主畫面」，之後一按即達。</Tip>
    </Step>
    <Step n={2} title="認識首頁">
      <p>登入後會見到左側選單：<strong>物業管理</strong>（你日常最主要的工作區）、<strong>媒體庫</strong>（圖片倉庫）。</p>
      <Shot src="/manual/login.png" caption="後台登入頁" />
    </Step>
    <Step n={3} title="了解你的權限">
      <p>普通同事（Staff）只會看到<strong>分配給自己的物業</strong>——列表上方有藍色提示條顯示你的帳號。看不到某個盤？代表它未指派給你，找 Admin 分配即可。</p>
      <Done>可以開始了！下一步去「新增物業」章節學習上架流程。</Done>
    </Step>
  </div>
);

const CreateChapter = () => (
  <div>
    <h2 className="mb-1 text-2xl font-bold">新增物業</h2>
    <p className="mb-6 text-sm text-slate-500">上架一個新盤的完整流程，大約需要 10–15 分鐘。</p>
    <Shot src="/manual/editor-top.png" caption="物業編輯頁：基本資料欄位（負責人決定權限歸屬）" />
    <div className="my-6">
      <Step n={1} title="進入新增頁">
        <p>左側選單點「物業管理」→ 按藍色「<strong>+ 新增物業</strong>」按鈕。</p>
      </Step>
      <Step n={2} title="填寫基本資料">
        <p>物業編號、標題、租售類型（放售／放租／租售均可）、地區、地址、價錢、實用面積、詳細描述。</p>
        <Tip>描述文字可以照抄業主提供的資料，系統不會刪減——之後出 FB 貼文時也是原文完整顯示。</Tip>
      </Step>
      <Step n={3} title="上傳照片">
        <p>在「物業相簿」區按「+ 從媒體庫加入」→ 可以即場上載新相，或揀之前上傳過的相。<strong>第一張相就是封面</strong>，可以用 ↑↓ 按鈕調次序。</p>
        <Warn>所有上傳的照片會<strong>自動加上品牌防盜水印</strong>（中央對角 MARVELAND），這是保護公司和業主盤源的措施，毋須自行處理。</Warn>
      </Step>
      <Step n={4} title="平面圖＋地圖連結（選填）">
        <p>有的話一併加上，客人睇盤資訊更齊全。</p>
      </Step>
      <Step n={5} title="儲存與預覽">
        <p>拉到最底按「儲存物業」。儲存前可以用右上角「前台預覽」檢查客人看到的效果。</p>
      </Step>
    </div>
    <Done>完成！盤源即刻在前台 www.marveland.hk 上線。</Done>
  </div>
);

const ManageChapter = () => (
  <div>
    <h2 className="mb-1 text-2xl font-bold">管理物業</h2>
    <p className="mb-6 text-sm text-slate-500">日常維護：編輯資料、更新狀態、刪除下架。</p>
    <Step n={1} title="狀態標記要及時">
      <p>盤租出了／售出了，馬上進入該物業編輯頁，把「狀態」改成對應標記——列表和前台都會即時顯示彩色徽章（租出／售出／獨家），避免客人問已成交的盤。</p>
    </Step>
    <Step n={2} title="編輯＝即時更新">
      <p>任何欄位改完按儲存，前台立即生效，毋須刷新等待。</p>
    </Step>
    <Step n={3} title="刪除 物業">
      <p>列表右側操作欄的垃圾桶按鈕。系統會自動清理不再被使用的照片，不用怕積垃圾。</p>
      <Warn>刪除是不可復原的。如果只是暫時不推，建議改狀態而不是刪除。</Warn>
    </Step>
    <Step n={4} title="認識列表徽章">
      <p>列表上可能見到：<span className="font-semibold text-blue-700">已推廣</span>（這個盤已發過 Facebook 貼文）、成效徽章（↗ 點擊／👁 瀏覽），詳情見「社群推廣」和「看成效數據」章節。</p>
    </Step>
    <Shot src="/manual/list-badges.png" caption="物業列表：狀態徽章與成效欄" />
  </div>
);

const SocialChapter = () => (
  <div>
    <h2 className="mb-1 text-2xl font-bold">社群推廣（Facebook）</h2>
    <p className="mb-6 text-sm text-slate-500">最大賣點功能：一鍵把盤源變成專業 FB 貼文，附自動文案與追蹤。</p>
    <Shot src="/manual/editor-social-collapsed.png" caption="捲到下方：社群推廣（Facebook）區塊與「已推廣」徽章" />
    <div className="my-6">
      <Step n={1} title="展開推廣區">
        <p>進入物業編輯頁 → 捲到下方找到「<strong>社群推廣（Facebook）</strong>」區塊，點一下標題列展開。右上角綠色「已推廣」徽章表示這個盤發過貼文。</p>
      </Step>
      <Step n={2} title="揀相（最多 10 張）">
        <p>縮圖牆直接點選——藍框＝已選，預設全選。不想出某張就點掉它。</p>
      </Step>
      <Step n={3} title="檢查貼文文案與 Hashtag">
        <p>系統自動生成整套文案：標題、售租價、面積、地址、盤源描述、<strong>短連結</strong>、Hashtag；灰色區是<strong>最終效果預覽</strong>，旁邊有「複製文案」按鈕方便先傳業主過目。</p>
        <Tip>Hashtag 用「點選標籤牆」組合——地區（#葵芳）、類型（#工廈）、特色（#高樓底）逐個點加入，已選會在上方列出，點 × 移除。</Tip>
      </Step>
      <Shot src="/manual/social-hashtag-wall.png" caption="文案預覽（含短連結）＋Hashtag 標籤牆＋發佈按鈕實景" />
      <Step n={4} title="發佈！">
        <p>按藍色「發佈到 Facebook」→ 彈出紅色確認框（提醒將公開發佈）→ 按「確認公開發佈」。</p>
        <Warn>發佈是公開的、所有訪客可見。如果發現有錯：到 FB 專頁刪掉那則貼文，回來改好再發一次即可。</Warn>
      </Step>
      <Step n={5} title="完成的標記">
        <p>成功後區塊頂部會出現「<strong>已推廣</strong>＋日期」綠色徽章，物業列表同時顯示藍色「已推廣」印仔。再按一次按鈕可以重新發佈新版貼文。</p>
      </Step>
    </div>
    <Done>貼文裡的「立即了解」短連結會自動追蹤點擊——數據在哪看？去下一章！</Done>
  </div>
);

const AnalyticsChapter = () => (
  <div>
    <h2 className="mb-1 text-2xl font-bold">看成效數據</h2>
    <p className="mb-6 text-sm text-slate-500">兩個數字，一眼知道邊個盤最受歡迎。</p>
    <Shot src="/manual/list-badges.png" caption="物業列表的「成效」欄" />
    <div className="my-6 space-y-4">
      <div className="rounded-xl border border-slate-200 p-4">
        <h4 className="font-bold text-slate-900">↗ FB 點擊（藍色徽章）</h4>
        <p className="mt-1 text-sm text-slate-600">有人從你的 Facebook 貼文點「立即了解」入來睇盤的次數。越多＝貼文越吸引。</p>
      </div>
      <div className="rounded-xl border border-slate-200 p-4">
        <h4 className="font-bold text-slate-900">👁 頁面瀏覽（灰色徽章）</h4>
        <p className="mt-1 text-sm text-slate-600">這個盤的物業頁被人打開的總次數（包括自然瀏覽）。前台物業頁標題旁也會顯示「N 人查看」，給客人製造搶手氣氛。</p>
      </div>
      <Tip>想比較哪個盤最熱？在物業管理列表掃一眼「成效」欄就行——排序靠上的就是明星盤，值得優先投放廣告費。</Tip>
    </div>
  </div>
);

const MediaChapter = () => (
  <div>
    <h2 className="mb-1 text-2xl font-bold">媒體庫</h2>
    <p className="mb-6 text-sm text-slate-500">公司的圖片倉庫：所有物業照、logo 都存在這裡。</p>
    <Step n={1} title="什麼時候用">
      <p>其實多數時候你不需要單獨開媒體庫——新增／編輯物業時的「從媒體庫加入」就是同一批圖片。想集中整理（改名、刪舊圖）才進「媒體庫」頁面。</p>
    </Step>
    <Step n={2} title="上傳">
      <p>媒體庫頁按「上傳圖片」，支援多張一起。一樣會自動加水印。</p>
      <Tip>手機拍的相直接上傳就可以，系統會自動壓縮到手機快速載入的大小。</Tip>
    </Step>
    <Step n={3} title="刪除">
      <p>正在被物業使用的圖片會標示「已使用」，刪除前想清楚——刪了前台和已出的 FB 貼文都會缺圖。</p>
    </Step>
  </div>
);

const FaqChapter = () => (
  <div>
    <h2 className="mb-1 text-2xl font-bold">常見問題</h2>
    <p className="mb-6 text-sm text-slate-500">卡住了先看這裡，大部分問題一分鐘解決。</p>
    <div className="space-y-3">
      <Faq q="登入時說「登入已過期」？">
        點擊提示後會自動回到登入頁，重新輸入帳號密碼即可。離開太久忘記操作會自動登出，屬正常保安機制。
      </Faq>
      <Faq q="為什麼我看不到某個物業？">
        Staff 帳號只能看到「創建者／權限歸屬」是自己的物業。請 Admin 在該物業的編輯頁把它指派給你（負責人揀你），儲存後刷新即可。
      </Faq>
      <Faq q="FB 貼文發佈失敗怎麼辦？">
        看紅色錯誤文字：如果是「 unsupported 」或網絡訊息，先重試一次；仍失敗就截圖交給 Admin 處理（可能是 FB 授權到期，需 Admin 更新）。已發出的舊貼文不受影響。
      </Faq>
      <Faq q="上傳的照片為什麼有水印？還可以去掉嗎？">
        中央對角的 MARVELAND 水印是自動加上的品牌保護，防止盤相被同行盜用，不能移除。這也是我們跟客人講盤源保障的賣點。
      </Faq>
      <Faq q="我想讓某個盤有影片版貼文？">
        影片模式即將推出，目前以照片貼文為主。有需求先向 Admin 登記，推出時第一時間安排。
      </Faq>
      <Faq q="其他技術支援？">
        WhatsApp 聯絡管理員，並提供：哪個物業、做了什麼操作、看到的錯誤訊息截圖。
      </Faq>
    </div>
  </div>
);

// ---------- 主頁 ----------
const CHAPTER_COMPONENTS: Record<ChapterId, React.FC> = {
  start: StartChapter,
  create: CreateChapter,
  manage: ManageChapter,
  social: SocialChapter,
  analytics: AnalyticsChapter,
  media: MediaChapter,
  faq: FaqChapter,
};

const ManualPage: React.FC = () => {
  const [tab, setTab] = useState<ChapterId>(chapterFromHash());
  useEffect(() => {
    const onHash = () => setTab(chapterFromHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  const go = (id: ChapterId) => {
    setTab(id);
    window.location.hash = id;
    window.scrollTo({ top: 0 });
  };

  const idx = CHAPTERS.findIndex((c) => c.id === tab);
  const Body = CHAPTER_COMPONENTS[tab];

  return (
    <div className="min-h-screen bg-slate-100 font-sans text-slate-900">
      {/* 頂部 */}
      <header className="sticky top-0 z-10 border-b border-slate-200 bg-white shadow-sm">
        <div className="mx-auto max-w-4xl px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <img src="/logo.png" alt="" className="h-7 w-auto" />
              <div>
                <div className="text-base font-bold leading-tight">操作手冊</div>
                <div className="text-[11px] text-slate-400">金晉物業代理・後台培訓指南</div>
              </div>
            </div>
            <a href="https://marveland.hk/admin" target="_blank" rel="noreferrer" className="rounded-lg bg-blue-800 px-3 py-1.5 text-xs font-bold text-white hover:bg-blue-900">
              開啟後台 ↗
            </a>
          </div>
          {/* 分頁導航（手機可橫滑） */}
          <nav className="-mx-4 mt-3 flex gap-1 overflow-x-auto px-4 pb-1">
            {CHAPTERS.map((c, i) => (
              <button
                key={c.id}
                onClick={() => go(c.id)}
                className={`flex shrink-0 items-center gap-1.5 rounded-lg px-3 py-2 text-sm font-semibold transition ${
                  tab === c.id ? 'bg-blue-800 text-white' : 'text-slate-500 hover:bg-slate-100'
                }`}
              >
                <span className="text-xs opacity-70">{i + 1}</span>
                {c.icon}
                {c.label}
              </button>
            ))}
          </nav>
        </div>
      </header>

      {/* 主內容 */}
      <main className="mx-auto max-w-4xl px-4 py-8">
        <article className="rounded-2xl bg-white p-6 shadow-sm md:p-9">
          <Body />
        </article>

        {/* 上/下一章 */}
        <div className="mt-6 flex items-center justify-between">
          {idx > 0 ? (
            <button onClick={() => go(CHAPTERS[idx - 1].id)} className="rounded-lg bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50">
              ← {CHAPTERS[idx - 1].label}
            </button>
          ) : <span />}
          {idx < CHAPTERS.length - 1 && (
            <button onClick={() => go(CHAPTERS[idx + 1].id)} className="rounded-lg bg-blue-800 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-900">
              下一章：{CHAPTERS[idx + 1].label} →
            </button>
          )}
        </div>
      </main>

      <footer className="border-t border-slate-200 py-8 text-center text-xs text-slate-400">
        <div>Marveland 金晉物業代理 · 內部培訓教材 · 如有疑問請聯絡管理員</div>
        <div className="mt-2">
          Powered by{' '}
          <a href="https://jkdcoding.com" target="_blank" rel="noreferrer" className="font-semibold text-blue-700 hover:underline">
            JKD Coding
          </a>{' '}
          ・ 以無法為有法
        </div>
      </footer>
    </div>
  );
};

export default ManualPage;
